import { Box, IconButton, Stack, styled, Typography } from '@mui/material';
import { ConversationStatus } from '@provision/conversation-types';
import { useVisibilityChange } from '@uidotdev/usehooks';
import React, {
  memo,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useResizable } from 'react-resizable-layout';
import {
  BlockContainer,
  BlockContent,
  BlockHeader,
  CopyToClipboardButton,
  DynamicIcon,
  FormEditableText,
  Tooltip,
} from 'src/components';
import {
  getSidePanelScrollbarSx,
  RIGHT_PANEL_RESIZE_GUTTER_WIDTH,
} from 'src/components/EntitySidePanel';
import { GLOBAL_TABS_HEIGHT } from 'src/components/GlobalTabs/styled';
import { CollapsingLayoutBlock } from 'src/components/Layout/collapsing-layout-block.view';
import { DrawerCloseControl } from 'src/components/Navbar/DesktopDrawer';
import { ResizeHandle } from 'src/components/ResizeHandle';
import { GLASS_BLUR } from 'src/constants/glass';
import {
  Conversation,
  useConversation,
  useGroupRules,
  useResolvedAddress,
  UserGroupRules,
} from 'src/graphql';
import { useConversationUpdate } from 'src/graphql/entities/conversation/use-conversation-update';
import { useMarkMessagesAsSeen } from 'src/graphql/entities/conversation-aggregations';
import { useUsersPresenceCached } from 'src/graphql/entities/user-presence/hooks/use-users-presence';
import { Chat, ConversationSidePanel } from 'src/pages/Conversations';
import { GridBreadcrumbItem } from 'src/pages/CustomViews/custom-views-selected-view/base-selected-view';
import useConversationViewsStore from 'src/store/conversation-views';
import { useScreenDimensions } from 'src/utils/system';
import { ConversationWindowHeader } from './SidePanel/ConversationWindow/conversation-window-header';

export const RIGHT_PANEL_MIN_WIDTH_PX = 250;
export const RIGHT_PANEL_WIDTH_PX = 370;
export const RIGHT_PANEL_MAX_WIDTH_PX = 550;

const ConversationGlassHeader = styled(BlockHeader, {
  name: 'ConversationGlassHeader',
})(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  zIndex: theme.zIndex.app.ServiceDeskConversationHeader,
  backgroundColor: 'transparent',
  backdropFilter: GLASS_BLUR,
  WebkitBackdropFilter: GLASS_BLUR,
}));

const NoticeMessageContainer = styled(Box, {
  name: 'NoticeMessageContainer',
})(({ theme }) => ({
  position: 'absolute',
  top: GLOBAL_TABS_HEIGHT,
  width: '100%',
  padding: theme.spacing(1),
  zIndex: '100',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: theme.palette.custom.OV.sys.primaryContainer,
}));

const NoticeMessageText = styled(Typography, {
  name: 'NoticeMessageText',
})(({ theme }) => ({
  color: theme.palette.custom.OV.sys.onPrimaryContainer,
}));

const ConversationContainer = styled(Stack, {
  name: 'ConversationContainer',
})(({ theme }) => ({
  position: 'relative',
  flexDirection: 'row',
  flex: 1,
  zIndex: theme.zIndex.app.ServiceDeskConversation,
  width: 0,
}));

// The personalize tool floats over the cards just under the glass header; it must sit
// inside the column's own scroller, or `sticky` resolves against the wrong box.
const FLOATING_TOOLS_TOP = `calc(${GLOBAL_TABS_HEIGHT} + 0.625rem)`;

const ConversationDetailsWrapper = styled(CollapsingLayoutBlock, {
  name: 'ConversationDetailsWrapper',
})(({ theme, open }) => ({
  position: 'static',
  [theme.breakpoints.down('sm')]: {
    position: 'absolute',
    // pinned between the header and the container's bottom: a height of 100% plus a
    // header-sized offset would push the panel's tail past the viewport, out of reach
    top: GLOBAL_TABS_HEIGHT,
    bottom: 0,
    height: 'auto',

    /*
        Avoid visual blending of conversation details block with the conversation
        view during opening/closing animation.
        `right` offset hides the border when panel is completely opened/closed.
      */
    right: -1,
    ...(!open ? {} : { right: 1 }),
  },
}));

interface ConversationProps {
  conversation: Conversation;
  onCloseConversation: () => void;
  onDeleteConversation?: () => void;
  noticeMessage?: string;
  onUpdate: (c: Conversation) => Promise<void>;
  isConversationExpanded?: boolean;
  isSplitView?: boolean;
  customViewId?: string | null;
  customViewName?: string | null;
}

const ConversationWindowComponent = ({
  isConversationExpanded,
  conversation,
  onCloseConversation,
  noticeMessage,
  onUpdate,
  customViewId,
  customViewName,
  isSplitView,
  onDeleteConversation,
}: ConversationProps): React.JSX.Element => {
  const { isMobile, isDesktop } = useScreenDimensions();
  const { conversationSidePanelStateOpen, setConversationSidePanelStateOpen } =
    useConversationViewsStore();

  useEffect(() => {
    if (isMobile) {
      setConversationSidePanelStateOpen(false);
    }
  }, [isMobile, setConversationSidePanelStateOpen]);

  const [isNoticeMessageClosed, setIsNoticeMessageClosed] = useState(false);
  const handleAfterDelete = onDeleteConversation || onCloseConversation;
  const { updateConversation } = useConversationUpdate();
  const { resolvedAddress, loading: isResolvingAddress } = useResolvedAddress(
    conversation.address,
  );
  const resolvedConversation = useMemo(
    () =>
      resolvedAddress && conversation.address !== resolvedAddress
        ? { ...conversation, address: resolvedAddress }
        : conversation,
    [conversation, resolvedAddress],
  );

  const { submitConversationData, participants, loading } = useConversation({
    conversationId: resolvedConversation.conversationId,
    partnerId: resolvedConversation?.partnerId,
  });

  const { markAsSeenIfNeeded } = useMarkMessagesAsSeen(resolvedConversation.conversationId);
  const isDocumentVisible = useVisibilityChange();

  useEffect(() => {
    // Marking seen while hidden would zero the unseen count before the agent reads it.
    if (!isDocumentVisible) return;

    void markAsSeenIfNeeded(resolvedConversation.conversationId);
  }, [resolvedConversation, markAsSeenIfNeeded, isDocumentVisible]);

  const onSubmitEditedSubject = useCallback(
    async (data: { subject: string }) => {
      const { subject } = data;

      if (subject && resolvedConversation?.subject !== subject) {
        await updateConversation({ ...resolvedConversation, subject });
      }
    },
    [updateConversation, resolvedConversation],
  );

  const form = useForm({
    values: { subject: resolvedConversation?.subject },
  });

  useEffect(() => {
    form.reset({ subject: resolvedConversation?.subject });
    setIsNoticeMessageClosed(false);
  }, [resolvedConversation?.subject, form]);

  const conversationViewRef = useRef<HTMLDivElement | null>(null);

  const {
    position: sidePanelWidth,
    separatorProps,
    isDragging,
  } = useResizable({
    axis: 'x',
    initial: RIGHT_PANEL_WIDTH_PX,
    min: RIGHT_PANEL_MIN_WIDTH_PX,
    max: RIGHT_PANEL_MAX_WIDTH_PX,
    containerRef: conversationViewRef as RefObject<HTMLElement>,
    reverse: true,
    disabled: !conversationSidePanelStateOpen || isMobile,
  });

  const canResizeDetailsView = conversationSidePanelStateOpen && isDesktop;

  const { groupRules } = useGroupRules();

  const userIds = participants
    .filter((i) => i.participantEntity === 'platform_user' && i.participantIdentity)
    .map((i) => i.participantIdentity);

  const { userPresenceMap } = useUsersPresenceCached(userIds);

  const shouldDisableSubjectChange =
    isResolvingAddress ||
    (resolvedConversation.conversationStatusId as ConversationStatus) ===
      ConversationStatus.Closed ||
    groupRules[UserGroupRules.AccessToUpdateConversation] === false ||
    resolvedConversation.isDeleted;

  const [panelToolsSlot, setPanelToolsSlot] = useState<HTMLElement | null>(null);

  return (
    <ConversationContainer
      ref={conversationViewRef}
      sx={{
        userSelect: isDragging ? 'none' : 'auto',
      }}
    >
      <BlockContainer
        framed={isSplitView || isConversationExpanded}
        sx={{ minWidth: '20rem', position: 'relative' }}
      >
        <ConversationGlassHeader>
          <DrawerCloseControl />
          <Tooltip title="Close Conversation">
            <DynamicIcon
              name="right_panel_close"
              sx={{
                cursor: 'pointer',
                display: { sm: 'none' },
              }}
              onClick={onCloseConversation}
            />
          </Tooltip>
          {!isMobile && isConversationExpanded ? (
            <GridBreadcrumbItem
              handleClick={onCloseConversation}
              text={customViewName || 'All'}
            />
          ) : null}
          <CopyToClipboardButton
            content={resolvedConversation.friendlyId}
            successMessage={`Copied "${resolvedConversation.friendlyId}" to clipboard`}
          >
            {resolvedConversation.friendlyId}
          </CopyToClipboardButton>
          <Box flex="1" sx={{ overflow: 'hidden' }}>
            <FormProvider {...form}>
              <FormEditableText
                type="editable-text"
                defaultValue={resolvedConversation.subject}
                disabled={shouldDisableSubjectChange}
                name="subject"
                onBlur={form.handleSubmit(onSubmitEditedSubject)}
                textFieldProps={{
                  inputProps: {
                    maxLength: 200,
                  },
                }}
              />
            </FormProvider>
          </Box>
          <ConversationWindowHeader
            isConversationExpanded={isConversationExpanded}
            customViewId={customViewId}
            conversation={resolvedConversation}
            isConversationDetailsOpen={conversationSidePanelStateOpen}
            onConversationDetailsToggle={setConversationSidePanelStateOpen}
            handleAfterDelete={handleAfterDelete}
          />
        </ConversationGlassHeader>
        <BlockContent
          sx={{
            p: 0,
            justifyContent: 'end',
            position: 'static',
          }}
        >
          <Box
            display="flex"
            flex="1"
            flexDirection="row"
            justifyContent="space-between"
            overflow="hidden"
          >
            <Box
              sx={{
                position: 'relative',
                width:
                  !isMobile && conversationSidePanelStateOpen
                    ? `calc(100% - ${Math.max(sidePanelWidth, 0)}px - ${RIGHT_PANEL_RESIZE_GUTTER_WIDTH})`
                    : '100%',
                height: '100%',
              }}
            >
              {!loading && !!noticeMessage && !isNoticeMessageClosed && (
                <NoticeMessageContainer>
                  <NoticeMessageText variant="body2">{noticeMessage}</NoticeMessageText>
                  <IconButton onClick={() => setIsNoticeMessageClosed(true)}>
                    <DynamicIcon name="close" />
                  </IconButton>
                </NoticeMessageContainer>
              )}
              <Chat
                key={resolvedConversation.conversationId}
                conversation={resolvedConversation}
                participants={participants}
                userPresenceMap={userPresenceMap}
                onSubmitConversationData={submitConversationData}
                onPermanentlyDeleted={handleAfterDelete}
              />
            </Box>

            {canResizeDetailsView ? (
              // The cards carry the only frame now; keep the handle out of the
              // header band so it doesn't cut across the glass bar.
              <Box
                sx={{
                  position: 'relative',
                  flexShrink: 0,
                  width: RIGHT_PANEL_RESIZE_GUTTER_WIDTH,
                }}
              >
                <ResizeHandle
                  axis="x"
                  isDragging={isDragging}
                  {...separatorProps}
                  sx={{ left: 0, right: 0, top: GLOBAL_TABS_HEIGHT }}
                />
              </Box>
            ) : null}

            <ConversationDetailsWrapper
              open={conversationSidePanelStateOpen}
              sx={(theme) => ({
                width: isMobile ? '100%' : sidePanelWidth,
                // The stored width can exceed what the row has left (narrow container,
                // wide drag); without this cap the panel's right edge — and its
                // scrollbar — get pushed past the clipping box instead of the panel
                // giving up width on its left side.
                ...(!isMobile && {
                  maxWidth: `calc(100% - ${RIGHT_PANEL_RESIZE_GUTTER_WIDTH})`,
                }),
                ...(isDragging && { transition: 'none' }),
                zIndex: isMobile ? theme.zIndex.app.ServiceDeskConversationDetails : undefined,
                // This column is the scroller: the cards inside grow to content height,
                // and on desktop they slide under the glass header.
                overflow: conversationSidePanelStateOpen ? 'hidden auto' : 'hidden',
                ...getSidePanelScrollbarSx(),
                ...(isMobile
                  ? {
                      // Mobile is an overlay pinned by the wrapper's own top/bottom.
                      backgroundColor: theme.palette.custom.OV.sys.surfaceContainerLowest,
                    }
                  : { height: '100%' }),
              })}
            >
              <Box
                ref={setPanelToolsSlot}
                sx={{
                  position: 'sticky',
                  top: isMobile ? '0.5rem' : FLOATING_TOOLS_TOP,
                  zIndex: 3,
                  height: 0,
                  display: 'flex',
                  // the slot has no height on purpose; centering would lift the tool into the header
                  alignItems: 'flex-start',
                  justifyContent: 'flex-end',
                  gap: '0.5rem',
                  paddingRight: '0.875rem',
                  pointerEvents: 'none',
                  '& > *': { pointerEvents: 'auto' },
                }}
              />
              <Box
                sx={{
                  pt: isMobile ? '0.5rem' : `calc(${GLOBAL_TABS_HEIGHT} + 0.5rem)`,
                  pr: isMobile ? 0 : '0.25rem',
                  pb: '0.5rem',
                }}
              >
                <ConversationSidePanel
                  onUpdate={onUpdate}
                  conversation={resolvedConversation}
                  toolsSlot={panelToolsSlot}
                />
              </Box>
            </ConversationDetailsWrapper>
          </Box>
        </BlockContent>
      </BlockContainer>
    </ConversationContainer>
  );
};

export const ConversationWindow = memo(ConversationWindowComponent);
